import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Phone, Star } from "lucide-react";
import { HERO_IMAGE, INFO } from "@/data/content";
import { getStatus, scrollToHash } from "@/utils/hours";

const LINES = [
  [
    { text: "Il ritmo ", accent: false },
    { text: "buono", accent: true },
  ],
  [{ text: "della tua giornata.", accent: false }],
];

export const Hero = () => {
  const ref = useRef(null);
  const [status, setStatus] = useState(() => getStatus());

  useEffect(() => {
    const t = setInterval(() => setStatus(getStatus()), 60_000);
    return () => clearInterval(t);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <motion.img
          src={HERO_IMAGE}
          alt="Espresso e brioche al Bar Incontro"
          className="h-full w-full object-cover"
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/55 to-espresso/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso/80 via-espresso/20 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-8 sm:pb-28"
      >
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-sand/25 bg-espresso/40 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-sand backdrop-blur"
          >
            <MapPin className="size-3.5" />
            Via Togliatti 19 · Pesaro
          </motion.span>
          <motion.span
            data-testid="hero-status-badge"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-sand/25 bg-espresso/40 px-4 py-1.5 text-xs text-cream/85 backdrop-blur"
          >
            <span
              className={`size-1.5 rounded-full ${
                status.open ? "bg-[#2ECC71]" : "bg-[#E74C3C]"
              }`}
            />
            {status.text}
          </motion.span>
        </div>

        <h1 className="max-w-4xl font-serif text-[clamp(2.9rem,8vw,6.5rem)] font-semibold leading-[1.02] tracking-tight text-cream">
          {LINES.map((line, li) => (
            <span key={li} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + li * 0.14, duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.map((w, wi) => (
                  <span
                    key={wi}
                    className={w.accent ? "italic text-terra-light" : undefined}
                  >
                    {w.text}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
        >
          Espresso e brioche calde dal primo sguardo del mattino, dolci
          tutto il giorno e spritz al tramonto. Un punto di incontro per
          ogni momento, dalle 6:30 a mezzanotte.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-menu-button"
            onClick={() => scrollToHash("#menu")}
            className="rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-terra-light hover:shadow-[0_8px_30px_rgba(147,48,32,0.35)]"
          >
            Esplora il Menu
          </button>
          <button
            data-testid="hero-cta-directions-button"
            onClick={() => scrollToHash("#contatti")}
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-cream/10"
          >
            <MapPin className="size-4" />
            Come Raggiungerci
          </button>
          <a
            href={INFO.phoneHref}
            data-testid="hero-phone-link"
            className="inline-flex items-center gap-2 rounded-full border border-sand/25 bg-espresso/40 px-5 py-3 text-sm text-cream/85 backdrop-blur transition-colors duration-300 hover:bg-espresso/60"
          >
            <Phone className="size-4 text-sand" />
            {INFO.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-8 inline-flex items-center gap-2 text-sm text-cream/80"
        >
          <span className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="size-4 fill-gold text-gold" />
            ))}
          </span>
          <span className="font-semibold text-cream">{INFO.rating}</span>
          <span>· {INFO.reviewCount} recensioni su Google</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/50 md:flex"
      >
        Scorri
        <ArrowDown className="size-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
